import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useActiveFamily } from '../../../context/ActiveFamilyContext';
import { PlanComparisonResult } from '../../../services/planComparisonService';
import { DowngradeSelections, DowngradeRecommendations } from '../../../services/downgradeService';

interface Checklist {
  id: string;
  name: string;
  icon: string;
  points: number;
}

interface ChecklistSelectionStepProps {
  comparison: PlanComparisonResult;
  recommendations: DowngradeRecommendations | null;
  selections: DowngradeSelections;
  onUpdateSelection: (key: keyof DowngradeSelections, value: any) => void;
}

export default function ChecklistSelectionStep({
  comparison,
  recommendations,
  selections,
  onUpdateSelection,
}: ChecklistSelectionStepProps) {
  const { activeFamily } = useActiveFamily();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const checklistsChange = comparison.changes.find(c => c.feature === 'checklists');
  const limit = checklistsChange?.newLimit || 5;
  const selectedIds = selections.activeChecklistIds || [];
  const selectedCount = selectedIds.length;
  const isValid = selectedCount === limit;
  
  useEffect(() => {
    loadChecklists();
  }, [activeFamily?.id]);
  
  const loadChecklists = async () => {
    if (!activeFamily?.id) return;
    
    setLoading(true);
    try {
      const q = query(
        collection(db, 'checklist'),
        where('familyId', '==', activeFamily.id)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Checklist[];
      
      setChecklists(items);
    } catch (error) {
      console.error('Error cargando checklists:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredChecklists = checklists
    .filter(checklist =>
      checklist.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aRec = recommendations?.checklistIds.includes(a.id) ? 1 : 0;
      const bRec = recommendations?.checklistIds.includes(b.id) ? 1 : 0;
      if (aRec !== bRec) return bRec - aRec;
      return a.name.localeCompare(b.name);
    });
  
  const handleToggle = (checklistId: string) => {
    let newSelection: string[];
    
    if (selectedIds.includes(checklistId)) {
      newSelection = selectedIds.filter(id => id !== checklistId);
    } else {
      if (selectedCount < limit) {
        newSelection = [...selectedIds, checklistId];
      } else {
        return;
      }
    }
    
    onUpdateSelection('activeChecklistIds', newSelection);
  };
  
  const handleSelectRecommended = () => {
    if (recommendations?.checklistIds) {
      onUpdateSelection('activeChecklistIds', recommendations.checklistIds.slice(0, limit));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando checklists...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Selecciona {limit} Checklists
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          El plan gratuito permite {limit} checklists activos. Los demás se pausarán (no se borran).
        </p>
      </div>
      
      <div className={`rounded-xl p-4 border-2 ${
        isValid 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
          : selectedCount < limit
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
            : 'bg-red-50 dark:bg-red-900/20 border-red-500'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-3xl">
              {isValid ? '✅' : selectedCount < limit ? '📋' : '⚠️'}
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">
                {selectedCount} de {limit} seleccionados
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {isValid 
                  ? '¡Perfecto! Puedes continuar' 
                  : selectedCount < limit
                    ? `Selecciona ${limit - selectedCount} más`
                    : 'Has alcanzado el límite'}
              </div>
            </div>
          </div>
          
          {recommendations && recommendations.checklistIds.length >= limit && (
            <button
              onClick={handleSelectRecommended}
              className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              ⭐ Usar Recomendados
            </button>
          )}
        </div>
      </div>
      
      <div>
        <input
          type="text"
          placeholder="🔍 Buscar checklists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>
      
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredChecklists.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron checklists' : 'No tienes checklists'}
          </div>
        ) : (
          filteredChecklists.map(checklist => {
            const isSelected = selectedIds.includes(checklist.id);
            const isRecommended = recommendations?.checklistIds.includes(checklist.id);
            const canSelect = selectedCount < limit || isSelected;
            
            return (
              <button
                key={checklist.id}
                onClick={() => canSelect && handleToggle(checklist.id)}
                disabled={!canSelect}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 shadow-md'
                    : canSelect
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md'
                      : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-purple-600 border-purple-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && (
                      <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  
                  <div className="text-2xl flex-shrink-0">
                    {checklist.icon || '📋'}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white truncate">
                        {checklist.name}
                      </span>
                      {isRecommended && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                          🔸 RECOMENDADO
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      ⭐ {checklist.points} puntos
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      
      {checklists.length > limit && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">ℹ️</span>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="font-medium mb-1">
                ⏸️ {checklists.length - limit} checklists se pausarán
              </p>
              <p>
                Los checklists no seleccionados permanecerán guardados y podrás reactivarlos al renovar tu plan.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
